import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ThemeContext } from '../../../../context/ThemeContext';
import { LangContext } from '../../../../context/LangContext';
import { LoadingCircle } from '../../../../components/UI/Custom/LoadingCircle';
import { PermissionDenied } from '../../../../components/UI/Custom/PermissionDenied';
import { Notification } from '../../../../components/UI/Notifications';
import getLeadFilesApi from '../../../../utils/api/Lead/Files/getLeadFilesApi';
import getLeadFileByIdApi from '../../../../utils/api/Lead/Files/getLeadFileByIdApi';
import updateLeadFileVerificationApi from '../../../../utils/api/Lead/Files/updateLeadFileVerificationApi';
import { getLeadById } from '../../../../utils/api/Lead/getLeadById';
import { updateLeadField } from '../../../../utils/api/Lead/updateLead';
import { handleError } from '../../../../utils/custom/errorHandler';
import styles from '../../../../styles/pages/Lead/LeadManagement/VerificationPage.module.css';

const FILE_TYPES = [
  'user_picture',
  'field_identity_verification_fron',
  'field_identity_verification_back',
  'field_registration_address_front',
  'field_registration_address_back',
  'field_bank_statement',
  'field_credit_card_front_side',
  'field_credit_card_back_side',
];

const translations = {
  ru: {
    permissionDenied: 'Доступ запрещен',
    loadFilesError: 'Не удалось загрузить файлы верификации.',
    loadHistoryError: 'Не удалось загрузить историю файлов.',
    loadPreviewError: 'Не удалось загрузить предпросмотр файла.',
    unknownDate: 'Дата неизвестна',
    uploaded: 'Загружен',
    noUploads: 'Еще не загружен',
    notReviewed: 'Не проверен',
    approved: 'Подтвержден',
    rejected: 'Отклонен',
    history: 'История',
    preview: 'Предпросмотр',
    unnamedFile: 'Файл без названия',
    current: 'Текущий',
    open: 'Открыть',
    noFilesForType: 'Для этого типа файлов загрузок пока нет.',
    verificationFile: 'Файл верификации',
    pdfPreview: 'Предпросмотр PDF',
    previewNotAvailable: 'Предпросмотр недоступен для этого типа файла.',
    openOriginalFile: 'Открыть оригинальный файл',
    reviewTitle: 'Результат проверки',
    reviewHint: 'Статус и комментарий сохраняются для текущего файла этого типа.',
    reviewCommentLabel: 'Комментарий администратора',
    reviewCommentPlaceholder: 'Напишите причину подтверждения или отклонения...',
    reviewSave: 'Сохранить',
    reviewSaving: 'Сохранение...',
    reviewToggleLabel: 'Статус проверки',
    reviewPreview: 'Открыть текущий файл',
    reviewCurrentFile: 'Текущий файл',
    reviewCurrentFileMissing: 'Для этого типа еще нет текущего файла, поэтому сохранять статус пока некуда.',
    reviewUpdatedAt: 'Дата решения',
    reviewUploadDate: 'Загружен',
    reviewStatusLabel: 'Текущий статус',
    reviewSuccess: 'Статус верификации обновлен.',
    reviewError: 'Не удалось обновить статус верификации.',
    accountApprovalLabel: 'Аккаунт одобрен',
    accountApprovalHint: 'Отдельный статус для итогового одобрения аккаунта.',
    accountApprovalSuccess: 'Статус одобрения аккаунта обновлен.',
    accountApprovalError: 'Не удалось обновить статус одобрения аккаунта.',
    fileLabels: {
      user_picture: 'Фото',
      field_identity_verification_fron: 'Документ спереди',
      field_identity_verification_back: 'Документ сзади',
      field_registration_address_front: 'Адрес спереди',
      field_registration_address_back: 'Адрес сзади',
      field_bank_statement: 'Банковская выписка',
      field_credit_card_front_side: 'Карта спереди',
      field_credit_card_back_side: 'Карта сзади',
    },
  },
  en: {
    permissionDenied: 'Permission denied',
    loadFilesError: 'Failed to load verification files.',
    loadHistoryError: 'Failed to load file history.',
    loadPreviewError: 'Failed to load file preview.',
    unknownDate: 'Unknown date',
    uploaded: 'Uploaded',
    noUploads: 'No uploads yet',
    notReviewed: 'Not reviewed',
    approved: 'Approved',
    rejected: 'Rejected',
    history: 'History',
    preview: 'Preview',
    unnamedFile: 'Unnamed file',
    current: 'Current',
    open: 'Open',
    noFilesForType: 'No files uploaded for this type yet.',
    verificationFile: 'Verification file',
    pdfPreview: 'PDF preview',
    previewNotAvailable: 'Preview is not available for this file type.',
    openOriginalFile: 'Open original file',
    reviewTitle: 'Review result',
    reviewHint: 'The status and comment are saved for the current file of this type.',
    reviewCommentLabel: 'Admin comment',
    reviewCommentPlaceholder: 'Write why this file was approved or rejected...',
    reviewSave: 'Save',
    reviewSaving: 'Saving...',
    reviewToggleLabel: 'Review status',
    reviewPreview: 'Open current file',
    reviewCurrentFile: 'Current file',
    reviewCurrentFileMissing: 'There is no current file for this type yet, so there is nothing to review.',
    reviewUpdatedAt: 'Decision date',
    reviewUploadDate: 'Uploaded',
    reviewStatusLabel: 'Current status',
    reviewSuccess: 'Verification status updated.',
    reviewError: 'Failed to update verification status.',
    accountApprovalLabel: 'Account approved',
    accountApprovalHint: 'Separate final approval status for the whole account.',
    accountApprovalSuccess: 'Account approval status updated.',
    accountApprovalError: 'Failed to update account approval status.',
    fileLabels: {
      user_picture: 'Picture',
      field_identity_verification_fron: 'ID Front',
      field_identity_verification_back: 'ID Back',
      field_registration_address_front: 'Address Front',
      field_registration_address_back: 'Address Back',
      field_bank_statement: 'Bank Statement',
      field_credit_card_front_side: 'Card Front',
      field_credit_card_back_side: 'Card Back',
    },
  },
};

const IMAGE_MIME_PREFIX = 'image/';

const isImageFile = (file) => file?.mime_type?.startsWith(IMAGE_MIME_PREFIX);
const isPdfFile = (file) => file?.mime_type === 'application/pdf';

const renderFileTypePreview = (file, label) => {
  if (isImageFile(file) && file?.url) {
    return <img src={file.url} alt={label} className={styles.previewImage} />;
  }

  if (isPdfFile(file)) {
    return <PictureAsPdfIcon />;
  }

  return <DescriptionIcon />;
};

const formatDate = (value, unknownDateLabel) => {
  if (!value) {
    return unknownDateLabel;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return typeof value === 'string' ? value : unknownDateLabel;
  }

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const formatFileSize = (value) => {
  if (!value || Number.isNaN(Number(value))) {
    return null;
  }

  const size = Number(value);

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const extractCollection = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

const getReviewStatusLabel = (value, t) => {
  if (value === true) {
    return t.approved;
  }

  if (value === false) {
    return t.rejected;
  }

  return t.notReviewed;
};

const normalizeFile = (file) => ({
  ...file,
  url: file?.file_url || file?.url || null,
  size: file?.size_bytes ?? file?.size ?? null,
  created_at: file?.uploaded_at || file?.created_at || null,
});

const VerificationPage = () => {
  const { id } = useParams();
  const { theme } = useContext(ThemeContext);
  const { langSelected } = useContext(LangContext);
  const t = translations[langSelected] || translations.ru;
  const isDarkMode = theme === 'dark';
  const [currentFiles, setCurrentFiles] = useState([]);
  const [accountApproved, setAccountApproved] = useState(false);
  const [historyFiles, setHistoryFiles] = useState([]);
  const [selectedTypeKey, setSelectedTypeKey] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSavingReview, setIsSavingReview] = useState(false);
  const [isSavingAccountApproval, setIsSavingAccountApproval] = useState(false);
  const [isForbidden, setIsForbidden] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [reviewStatus, setReviewStatus] = useState(null);
  const [reviewComment, setReviewComment] = useState('');

  const fileTypes = useMemo(() => {
    return FILE_TYPES.map((typeKey) => ({
      key: typeKey,
      label: t.fileLabels[typeKey] || typeKey,
      currentFile: currentFiles.find((file) => file.type === typeKey) || null,
    }));
  }, [currentFiles, t]);

  const selectedType = useMemo(
    () => fileTypes.find((type) => type.key === selectedTypeKey) || null,
    [fileTypes, selectedTypeKey]
  );

  useEffect(() => {
    if (!selectedType) {
      setReviewStatus(null);
      setReviewComment('');
      return;
    }

    setReviewStatus(selectedType.currentFile?.is_confirmed ?? null);
    setReviewComment(selectedType.currentFile?.admin_comment || '');
  }, [selectedType]);

  const loadCurrentFiles = useCallback(async () => {
    const [filesResponse, leadResponse] = await Promise.all([
      getLeadFilesApi(id, {
        is_current: 1,
        per_page: 100,
      }),
      getLeadById(id, 'admin'),
    ]);

    setCurrentFiles(extractCollection(filesResponse).map(normalizeFile));
    setAccountApproved(Boolean(leadResponse?.account_verified));
  }, [id]);

  const loadHistoryFiles = useCallback(async (typeKey) => {
    const response = await getLeadFilesApi(id, {
      type: typeKey,
      per_page: 100,
    });

    setHistoryFiles(extractCollection(response).map(normalizeFile));
  }, [id]);

  useEffect(() => {
    let isMounted = true;

    const loadVerificationContext = async () => {
      setIsPageLoading(true);
      setErrors({});
      setSuccessMessage('');
      setIsForbidden(false);

      try {
        await loadCurrentFiles();

        if (!isMounted) {
          return;
        }
      } catch (requestError) {
        if (!isMounted) {
          return;
        }

        if (requestError.status === 403 || requestError.statusCode === 403) {
          setIsForbidden(true);
          return;
        }

        setErrors({ general: t.loadFilesError });
      } finally {
        if (isMounted) {
          setIsPageLoading(false);
        }
      }
    };

    loadVerificationContext();

    return () => {
      isMounted = false;
    };
  }, [loadCurrentFiles, t.loadFilesError]);

  const openHistoryModal = async (type) => {
    setSelectedTypeKey(type.key);
    setSelectedFile(null);
    setHistoryFiles([]);
    setIsHistoryLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      await loadHistoryFiles(type.key);
    } catch (requestError) {
      if (requestError.status === 403 || requestError.statusCode === 403) {
        setIsForbidden(true);
      } else {
        setErrors({ general: t.loadHistoryError });
      }
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const openPreviewModal = async (file) => {
    setIsPreviewLoading(true);
    setErrors({});

    try {
      const response = await getLeadFileByIdApi(id, file.id);
      setSelectedFile(normalizeFile(response?.data || response));
    } catch (requestError) {
      if (requestError.status === 403 || requestError.statusCode === 403) {
        setIsForbidden(true);
      } else {
        setErrors({ general: t.loadPreviewError });
      }
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const closeHistoryModal = () => {
    setSelectedTypeKey(null);
    setHistoryFiles([]);
    setSelectedFile(null);
    setIsHistoryLoading(false);
    setIsPreviewLoading(false);
    setIsSavingReview(false);
  };

  const closePreviewModal = () => {
    setSelectedFile(null);
    setIsPreviewLoading(false);
  };

  if (isForbidden) {
    return <PermissionDenied text={t.permissionDenied} />;
  }

  const handleSaveReview = async () => {
    if (!selectedType?.currentFile || reviewStatus === null) {
      return;
    }

    setIsSavingReview(true);
    setErrors({});
    setSuccessMessage('');

    const payload = {
      type: selectedType.key,
      is_confirmed: reviewStatus,
      comment: reviewComment.trim() || null,
    };

    try {
      const response = await updateLeadFileVerificationApi(id, payload);
      const updatedFile = normalizeFile(response?.data || response);

      await Promise.all([
        loadCurrentFiles(),
        loadHistoryFiles(selectedType.key),
      ]);

      setSelectedFile((previousFile) => (
        previousFile && previousFile.id === updatedFile.id ? updatedFile : previousFile
      ));

      setReviewStatus(updatedFile.is_confirmed ?? null);
      setReviewComment(updatedFile.admin_comment || '');
      setSuccessMessage(t.reviewSuccess);
    } catch (requestError) {
      handleError(
        {
          ...requestError,
          message: requestError?.message || t.reviewError,
        },
        setErrors
      );
    } finally {
      setIsSavingReview(false);
    }
  };

  const handleAccountApprovalToggle = async () => {
    const nextValue = !accountApproved;

    setIsSavingAccountApproval(true);
    setErrors({});
    setSuccessMessage('');

    try {
      await updateLeadField(id, 'account_verified', nextValue, 'admin');
      setAccountApproved(nextValue);
      setSuccessMessage(t.accountApprovalSuccess);
    } catch (requestError) {
      handleError(
        {
          ...requestError,
          message: requestError?.message || t.accountApprovalError,
        },
        setErrors
      );
    } finally {
      setIsSavingAccountApproval(false);
    }
  };

  return (
    <div className={`${styles.page} ${isDarkMode ? styles.dark : ''}`}>
      {errors.general ? (
        <div className={styles.notificationWrap}>
          <Notification type="error" message={errors.general} />
        </div>
      ) : null}
      {successMessage ? (
        <div className={styles.notificationWrap}>
          <Notification type="success" message={successMessage} />
        </div>
      ) : null}

      {isPageLoading ? (
        <div className={styles.loadingState}>
          <LoadingCircle />
        </div>
      ) : (
        <div className={styles.grid}>
          {fileTypes.map((type) => {
            const file = type.currentFile;
            const isApproved = file?.is_confirmed === true;
            const isRejected = file?.is_confirmed === false;

            return (
              <button
                key={type.key}
                type="button"
                className={`${styles.typeCard} ${isApproved ? styles.approvedTypeCard : ''} ${isApproved && isDarkMode ? styles.darkApprovedTypeCard : ''} ${isRejected ? styles.rejectedTypeCard : ''} ${isRejected && isDarkMode ? styles.darkRejectedTypeCard : ''}`}
                onClick={() => openHistoryModal(type)}
              >
                <div className={styles.previewWrap}>
                  <div className={styles.previewFallback}>
                    {renderFileTypePreview(file, type.label)}
                  </div>
                </div>
                <div className={styles.typeMeta}>
                  <span className={styles.typeNameRow}>
                    <span className={styles.typeName}>{type.label}</span>
                  </span>
                  <span className={`${styles.typeStatus} ${isApproved ? styles.approvedTypeStatus : ''}`}>
                    {file ? getReviewStatusLabel(file.is_confirmed, t) : t.noUploads}
                  </span>
                </div>
              </button>
            );
          })}

          <div
            className={`${styles.typeCard} ${accountApproved ? styles.approvedTypeCard : ''} ${accountApproved && isDarkMode ? styles.darkApprovedTypeCard : ''} ${styles.accountApprovalCard}`}
          >
            <div className={styles.typeMeta}>
              <span className={styles.typeName}>{t.accountApprovalLabel}</span>
              <div className={styles.accountApprovalControl}>
                <button
                  type="button"
                  className={`${styles.accountApprovalButton} ${accountApproved ? styles.accountApprovalButtonActive : ''} ${isDarkMode ? styles.darkAccountApprovalButton : ''} ${accountApproved && isDarkMode ? styles.darkAccountApprovalButtonActive : ''}`}
                  onClick={handleAccountApprovalToggle}
                  disabled={isSavingAccountApproval}
                >
                  <span className={`${styles.accountApprovalButtonLabel} ${accountApproved ? styles.accountApprovalButtonLabelActive : ''}`}>
                    {accountApproved ? t.approved : t.rejected}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedType ? (
        <div className={styles.modalBackdrop} onClick={closeHistoryModal}>
          <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2 className={styles.modalTitle}>{selectedType.label}</h2>
              </div>
              <button type="button" className={styles.closeButton} onClick={closeHistoryModal}>
                <CloseIcon fontSize="inherit" />
              </button>
            </div>

            {isHistoryLoading ? (
              <div className={styles.modalLoading}>
                <LoadingCircle />
              </div>
            ) : (
              <div className={styles.modalContentStack}>
                <section className={styles.reviewPanel}>

                  {selectedType.currentFile ? (
                    <>
                      <div className={styles.statusField}>
                        <span className={styles.statusFieldLabel}>{t.reviewToggleLabel}</span>
                        <button
                          type="button"
                          className={`${styles.statusButton} ${reviewStatus === true ? styles.statusButtonActive : ''} ${theme === 'dark' ? styles.darkStatusButton : ''} ${reviewStatus === true && theme === 'dark' ? styles.darkStatusButtonActive : ''}`}
                          onClick={() => setReviewStatus((previousStatus) => previousStatus !== true)}
                        >
                          <span className={`${styles.statusButtonLabel} ${reviewStatus === true ? styles.statusButtonLabelActive : ''}`}>
                            {reviewStatus === true ? t.approved : t.rejected}
                          </span>
                        </button>
                      </div>

                      <label className={styles.textareaLabel}>
                        <span>{t.reviewCommentLabel}</span>
                        <textarea
                          className={styles.reviewTextarea}
                          value={reviewComment}
                          onChange={(event) => setReviewComment(event.target.value)}
                          placeholder={t.reviewCommentPlaceholder}
                        />
                      </label>

                      <div className={styles.reviewActions}>
                        <button
                          type="button"
                          className={styles.saveButton}
                          onClick={handleSaveReview}
                          disabled={isSavingReview || reviewStatus === null}
                        >
                          {isSavingReview ? t.reviewSaving : t.reviewSave}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className={styles.emptyState}>{t.reviewCurrentFileMissing}</div>
                  )}
                </section>

                {historyFiles.length ? (
                  <div className={styles.fileList}>
                    {historyFiles.map((file) => (
                      <button
                        key={file.id}
                        type="button"
                        className={styles.fileRow}
                        onClick={() => openPreviewModal(file)}
                      >
                        <div>
                          <span className={styles.fileName}>{file.original_name || file.name || t.unnamedFile}</span>
                          <span className={styles.fileMeta}>
                            {formatDate(file.created_at, t.unknownDate)}
                            {formatFileSize(file.size) ? ` • ${formatFileSize(file.size)}` : ''}
                            {file.is_current ? ` • ${t.current}` : ''}
                            {` • ${getReviewStatusLabel(file.is_confirmed, t)}`}
                          </span>
                        </div>
                        <span className={styles.fileAction}>{t.open}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyState}>{t.noFilesForType}</div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {selectedFile || isPreviewLoading ? (
        <div className={styles.modalBackdrop} onClick={closePreviewModal}>
          <div className={`${styles.modal} ${styles.previewModal}`} onClick={(event) => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <p className={styles.modalEyebrow}>{t.preview}</p>
                <h2 className={styles.modalTitle}>
                  {selectedFile?.original_name || selectedFile?.name || t.verificationFile}
                </h2>
              </div>
              <button type="button" className={styles.closeButton} onClick={closePreviewModal}>
                <CloseIcon fontSize="inherit" />
              </button>
            </div>

            {isPreviewLoading ? (
              <div className={styles.modalLoading}>
                <LoadingCircle />
              </div>
            ) : selectedFile ? (
              <div className={styles.previewContent}>
                {isImageFile(selectedFile) && selectedFile.url ? (
                  <img
                    src={selectedFile.url}
                    alt={selectedFile.original_name || selectedFile.name || t.verificationFile}
                    className={styles.previewLargeImage}
                  />
                ) : null}

                {isPdfFile(selectedFile) && selectedFile.url ? (
                  <iframe title={selectedFile.original_name || t.pdfPreview} src={selectedFile.url} className={styles.previewFrame} />
                ) : null}

                {!isImageFile(selectedFile) && !isPdfFile(selectedFile) ? (
                  <div className={styles.unsupportedState}>
                    <DescriptionIcon className={styles.unsupportedIcon} />
                    <p>{t.previewNotAvailable}</p>
                  </div>
                ) : null}

                {selectedFile.url ? (
                  <a
                    href={selectedFile.url}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.openLink}
                  >
                    {t.openOriginalFile}
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export { VerificationPage };
export default VerificationPage;
